import React from 'react';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { Table } from 'antd';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { EmployeeReport as EmployeeReportType } from 'utils/apiClient/services/reports/types';
import { euroFormatter } from 'Reports/utils';
import { SorterResult } from 'antd/es/table/interface';

const columns: ColumnsType<EmployeeReportType> = [
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="User" />
      </span>
    ),
    dataIndex: 'name',
    key: 'name',
    width: '30%',
    render: (value: EmployeeReportType['name']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Products Sold" />
      </span>
    ),
    dataIndex: 'product_sold_total',
    key: 'product_sold_total',
    width: '30%',
    render: (value: EmployeeReportType['product_sold_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Products Average" />
      </span>
    ),
    dataIndex: 'product_sold_avg',
    key: 'product_sold_avg',
    width: '30%',
    render: (value: EmployeeReportType['product_sold_avg']) => (
      <FormattedNumber
        value={Number(value)}
        minimumFractionDigits={2}
        maximumFractionDigits={2}
      />
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Retail Revenue" />
      </span>
    ),
    dataIndex: 'retail_revenue_total',
    key: 'retail_revenue_total',
    width: '30%',
    render: (value: EmployeeReportType['retail_revenue_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Retail Average" />
      </span>
    ),
    dataIndex: 'retail_revenue_avg',
    key: 'retail_revenue_avg',
    width: '30%',
    render: (value: EmployeeReportType['retail_revenue_avg']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Services Performed" />
      </span>
    ),
    dataIndex: 'service_count_total',
    key: 'service_count_total',
    width: '30%',
    render: (value: EmployeeReportType['service_count_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Services Average" />
      </span>
    ),
    dataIndex: 'service_count_avg',
    key: 'service_count_avg',
    width: '30%',
    render: (value: EmployeeReportType['service_count_avg']) => (
      <FormattedNumber
        value={Number(value)}
        minimumFractionDigits={2}
        maximumFractionDigits={2}
      />
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Services Revenue" />
      </span>
    ),
    dataIndex: 'service_revenue_total',
    key: 'service_revenue_total',
    width: '30%',
    render: (value: EmployeeReportType['service_revenue_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Services Revenue Average" />
      </span>
    ),
    dataIndex: 'service_revenue_avg',
    key: 'service_revenue_avg',
    width: '30%',
    render: (value: EmployeeReportType['service_revenue_avg']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Invoices" />
      </span>
    ),
    dataIndex: 'invoices_total',
    key: 'invoices_total',
    width: '30%',
    render: (value: EmployeeReportType['invoices_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Revenue Overall" />
      </span>
    ),
    dataIndex: 'revenue_overall_total',
    key: 'revenue_overall_total',
    width: '30%',
    sorter: true,
    sortDirections: ['ascend', 'descend'],
    render: (value: EmployeeReportType['revenue_overall_total']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
  {
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        <FormattedMessage defaultMessage="Average Basket" />
      </span>
    ),
    dataIndex: 'revenue_overall_avg',
    key: 'revenue_overall_avg',
    width: '30%',
    render: (value: EmployeeReportType['revenue_overall_avg']) => (
      <span style={{ whiteSpace: 'nowrap' }}>{euroFormatter(value)}</span>
    ),
  },
];

type EmployeeReportTableProps = {
  data: EmployeeReportType[];
  isLoading: boolean;
  handleListTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter:
      | SorterResult<EmployeeReportType>
      | SorterResult<EmployeeReportType>[]
  ) => void;
  handleUserClick: (record: EmployeeReportType) => void;
};

const EmployeeReportTable = ({
  data,
  isLoading,
  handleListTableChange,
  handleUserClick,
}: EmployeeReportTableProps) => (
  <Table
    columns={columns}
    dataSource={data}
    loading={isLoading}
    onChange={handleListTableChange}
    rowKey="user_id"
    onRow={(record) => ({
      onClick: () => handleUserClick(record),
      style: { cursor: 'pointer' },
    })}
    pagination={false}
  />
);

export default EmployeeReportTable;
